/**
 * tariff — модель конструктора тарифов Orda OS (единый источник для лендинга,
 * кабинета кафе и супер-админки).
 *
 * Идея владельца (2026-08-21): клиент собирает тариф сам — база без столов
 * (профиль точки навынос), ползунок зала (столы + официант = главный платный
 * модуль), ползунок сотрудников, тумблеры доставка/AI/white-label.
 *
 * Цены живут в БД (`tariff_grid`, редактируются в супер-админке → Планы);
 * DEFAULT_TARIFF_GRID — фолбэк с теми же числами на случай недоступного RPC.
 * Сетка откалибрована так, что пресеты дают РОВНО цены планов `plans`:
 *   cafe (зал до 10)                      = 10 000 = Стартер
 *   зал до 10 + до 10 сотр. + доставка    = 15 000 = Доставка
 *   resto (до 50, до 20, доставка, AI)    = 25 000 = Про
 *   max (безлимиты + все модули)          = 40 000 = Энтерпрайз
 * SQL-двойник расчёта: `tariff_config_price(jsonb)` (миграция 00458) — при
 * правке ступеней менять синхронно.
 */

export type TariffHall = 0 | 1 | 2 | 3 | 4; // без зала / до 10 / до 30 / до 50 / безлимит
export type TariffStaff = 0 | 1 | 2 | 3; // до 5 (в базе) / до 10 / до 20 / безлимит

export interface TariffConfig {
  hall: TariffHall;
  staff: TariffStaff;
  delivery: boolean;
  ai: boolean;
  wl: boolean;
}

export type TariffGridKey =
  | "base"
  | "hall_10" | "hall_30" | "hall_50" | "hall_inf"
  | "staff_10" | "staff_20" | "staff_inf"
  | "mod_delivery" | "mod_ai" | "mod_wl";

export type TariffGrid = Record<TariffGridKey, number>;

/** Фолбэк-сетка (= seed `tariff_grid` в 00458). */
export const DEFAULT_TARIFF_GRID: TariffGrid = {
  base: 2000,
  hall_10: 8000,
  hall_30: 11000,
  hall_50: 13000,
  hall_inf: 16000,
  staff_10: 1500,
  staff_20: 3000,
  staff_inf: 4500,
  mod_delivery: 3500,
  mod_ai: 3500,
  mod_wl: 10500,
};

/** Ступень → ключ сетки (индекс 0 = входит в базу, ключа нет). */
const HALL_KEYS: (TariffGridKey | null)[] = [null, "hall_10", "hall_30", "hall_50", "hall_inf"];
const STAFF_KEYS: (TariffGridKey | null)[] = [null, "staff_10", "staff_20", "staff_inf"];
/** Ступень → лимит в plans.features (-1 = безлимит). */
export const HALL_TABLE_LIMITS = [0, 10, 30, 50, -1] as const;
export const STAFF_LIMITS = [5, 10, 20, -1] as const;

export const TARIFF_PRESETS = {
  /** Точка навынос (самсахана): только база. */
  takeaway: { hall: 0, staff: 0, delivery: false, ai: false, wl: false },
  /** Кафе с залом = Стартер. */
  cafe: { hall: 1, staff: 0, delivery: false, ai: false, wl: false },
  /** Ресторан = Про. */
  resto: { hall: 3, staff: 2, delivery: true, ai: true, wl: false },
  /** Всё включено = Энтерпрайз. */
  max: { hall: 4, staff: 3, delivery: true, ai: true, wl: true },
} as const satisfies Record<string, TariffConfig>;

export interface TariffLine {
  key: TariffGridKey;
  amount: number;
}

export interface TariffResult {
  total: number;
  lines: TariffLine[];
}

/** Сумма тарифа по конфигурации. `grid` — частичная сетка из БД поверх дефолта. */
export function computeTariff(config: TariffConfig, grid?: Partial<TariffGrid> | null): TariffResult {
  const g: TariffGrid = { ...DEFAULT_TARIFF_GRID, ...(grid ?? {}) };
  const lines: TariffLine[] = [{ key: "base", amount: g.base }];
  const push = (key: TariffGridKey | null) => {
    if (key && g[key] > 0) lines.push({ key, amount: g[key] });
  };
  push(HALL_KEYS[config.hall]);
  push(STAFF_KEYS[config.staff]);
  if (config.delivery) push("mod_delivery");
  if (config.ai) push("mod_ai");
  if (config.wl) push("mod_wl");
  return { total: lines.reduce((s, l) => s + l.amount, 0), lines };
}

export interface TariffFeatures {
  max_tables: number;
  max_staff: number;
  delivery_enabled: boolean;
  ai_enabled: boolean;
  white_label: boolean;
}

/** Конфигурация → plans.features (то, что энфорсит сервер, 00263/00370). */
export function tariffConfigToFeatures(config: TariffConfig): TariffFeatures {
  return {
    max_tables: HALL_TABLE_LIMITS[config.hall],
    max_staff: STAFF_LIMITS[config.staff],
    delivery_enabled: config.delivery,
    ai_enabled: config.ai,
    white_label: config.wl,
  };
}

/** Ближайшая ступень НЕ МЕНЬШЕ лимита (-1 = безлимит → последняя ступень). */
function stepFor(limit: number, steps: readonly number[]): number {
  if (limit < 0) return steps.length - 1;
  const i = steps.findIndex((s) => s >= 0 && s >= limit);
  return i < 0 ? steps.length - 1 : i;
}

/** plans.features (любого плана) → конфигурация ползунков. Отсутствующие ключи = пол (10 столов / 5 сотрудников). */
export function featuresToTariffConfig(features: Partial<Record<string, unknown>> | null | undefined): TariffConfig {
  const f = features ?? {};
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  return {
    hall: stepFor(num(f.max_tables, 10), HALL_TABLE_LIMITS) as TariffHall,
    staff: stepFor(num(f.max_staff, 5), STAFF_LIMITS) as TariffStaff,
    delivery: f.delivery_enabled === true,
    ai: f.ai_enabled === true,
    wl: f.white_label === true,
  };
}

/** Валидация внешнего объекта (payload RPC, localStorage). Мусор → null. */
export function normalizeTariffConfig(raw: unknown): TariffConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const hall = r.hall;
  const staff = r.staff;
  if (typeof hall !== "number" || !Number.isInteger(hall) || hall < 0 || hall > 4) return null;
  if (typeof staff !== "number" || !Number.isInteger(staff) || staff < 0 || staff > 3) return null;
  return {
    hall: hall as TariffHall,
    staff: staff as TariffStaff,
    delivery: r.delivery === true,
    ai: r.ai === true,
    wl: r.wl === true,
  };
}

/** Сетка из RPC get_tariff_grid → частичная типизированная сетка (чужие/битые ключи отбрасываются). */
export function parseTariffGrid(raw: unknown): Partial<TariffGrid> {
  if (!raw || typeof raw !== "object") return {};
  const out: Partial<TariffGrid> = {};
  for (const key of Object.keys(DEFAULT_TARIFF_GRID) as TariffGridKey[]) {
    const v = (raw as Record<string, unknown>)[key];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) out[key] = v;
  }
  return out;
}
